import { useCallback, useRef } from 'react';
import maplibregl, { Map, MapMouseEvent } from 'maplibre-gl';
import TeAraroaMap from '../Map/TeAraroaMap';

interface MeetingPointPickerProps {
  latitude?: number;
  longitude?: number;
  onChange: (lat: number | undefined, lng: number | undefined) => void;
}

/**
 * Pick the meeting point by clicking the trail map (with its KM markers) rather
 * than typing coordinates. Click to place, drag to fine-tune. Reuses the admin
 * TeAraroaMap; mirrors the members-portal LocationPicker / admin TidePointMap.
 */
export function MeetingPointPicker({ latitude, longitude, onChange }: MeetingPointPickerProps) {
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const mapRef = useRef<Map | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const hasLocation = latitude != null && longitude != null;

  const placeMarker = useCallback((map: Map, lng: number, lat: number) => {
    if (markerRef.current) {
      markerRef.current.setLngLat([lng, lat]);
      return;
    }
    const marker = new maplibregl.Marker({ color: '#FF7700', draggable: true })
      .setLngLat([lng, lat])
      .addTo(map);
    marker.on('dragend', () => {
      const p = marker.getLngLat();
      onChangeRef.current(Number(p.lat.toFixed(6)), Number(p.lng.toFixed(6)));
    });
    markerRef.current = marker;
  }, []);

  const handleMapLoad = useCallback((map: Map) => {
    mapRef.current = map;
    if (latitude != null && longitude != null) {
      placeMarker(map, longitude, latitude);
    }
    // Only the value at first mount matters — the map initialises once, and the
    // modal remounts this picker each time it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeMarker]);

  const handleMapClick = useCallback((event: MapMouseEvent) => {
    const { lng, lat } = event.lngLat;
    placeMarker(event.target as Map, lng, lat);
    onChangeRef.current(Number(lat.toFixed(6)), Number(lng.toFixed(6)));
  }, [placeMarker]);

  const clear = () => {
    markerRef.current?.remove();
    markerRef.current = null;
    onChangeRef.current(undefined, undefined);
    mapRef.current?.easeTo({ center: [172.6, -43.5], zoom: 5, duration: 500 });
  };

  return (
    <div className="space-y-2">
      <div className="[&_canvas]:!cursor-crosshair overflow-hidden rounded-lg border border-gray-300">
        <TeAraroaMap
          center={hasLocation ? [longitude!, latitude!] : [172.6, -43.5]}
          zoom={hasLocation ? 12 : 5}
          height="360px"
          onMapLoad={handleMapLoad}
          onMapClick={handleMapClick}
          showControls={true}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>
          {hasLocation
            ? `Meeting point: ${latitude!.toFixed(5)}, ${longitude!.toFixed(5)} — click or drag the pin to adjust.`
            : 'Click the map to drop the meeting-point pin.'}
        </span>
        {hasLocation && (
          <button type="button" onClick={clear} className="text-red-600 hover:text-red-800">
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
